/**
 * HelixGlobals
 *
 * Typed accessor for debug globals previously scattered as (window as any).__XXX__.
 * Centralizes all window-level debug state into one typed module.
 *
 * Also manages ability-system globals (__HELIX_ELEMENTS, __HELIX_CONDITIONS)
 * which use globalThis for circular-dependency avoidance between
 * ElementRegistry ↔ HitEffects ↔ AbilityBuilder.
 */

import type { ElementConfig, ElementType, ConditionKey } from '../systems/player/abilities/modifiers/types';
import type { ActionContext } from '../systems/player/abilities/types';
import type { AbilityLoadStatus } from '../systems/player/abilities/registration/abilityLoader';

export interface BootState {
  initCalled: boolean;
  gameCreated: boolean;
  scenesAdded: boolean;
  readyFired: boolean;
  sceneStarted: boolean;
  progress: number;
  stage: string;
  error: string | null;
  abilityRegistration: {
    status: 'registered' | 'failed';
    className: string;
    exportName?: string;
    error?: string;
  } | null;
  /** Per-id ability load status from the ability loader (debug panel, QA probes). */
  abilityStatus?: Readonly<Record<string, AbilityLoadStatus>>;
  timestamp: number;
}

interface HelixDebugGlobals {
  __GAME_HOST__: any | null;
  __GAME_BOOT__: BootState | null;
  __currentScene: unknown | null;
  __BOOT_PROFILE__: any;
  __HELIX_SCHEMA_GHOST__: boolean;
  __HELIX_LIFECYCLE__: any;
  __HELIX_MP_DIAGNOSTICS__: any;
  __HELIX_UI_CONTEXT: any;
  __HELIX_UI_CONTEXT_PROVIDER_ACTIVE: boolean;
  __gamePlayer?: any;
  CooldownStore?: any;
}

/** Globals shared via globalThis for circular-dep resolution in the ability system */
interface HelixAbilityGlobals {
  __HELIX_ELEMENTS: Record<ElementType, ElementConfig>;
  __HELIX_CONDITIONS: Record<ConditionKey, (ctx: ActionContext) => boolean>;
}

declare global {
  interface Window extends Partial<HelixDebugGlobals> {}
}

// Single cast at module level — all downstream access is fully typed
const _globalThis = globalThis as typeof globalThis & Partial<HelixAbilityGlobals>;

/** Type-safe read from window debug globals */
export function getHelixGlobal<K extends keyof HelixDebugGlobals>(key: K): HelixDebugGlobals[K] | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as any)[key];
}

/** Type-safe write to window debug globals */
export function setHelixGlobal<K extends keyof HelixDebugGlobals>(key: K, value: HelixDebugGlobals[K]): void {
  if (typeof window === 'undefined') return;
  (window as any)[key] = value;
}

// ── Ability System Globals ──────────────────────────────────────────────────

/** Get the element registry (registered by ElementRegistry.ts on import) */
export function getAbilityElements(): Record<ElementType, ElementConfig> {
  return _globalThis.__HELIX_ELEMENTS ?? {} as Record<ElementType, ElementConfig>;
}

/** Get the condition registry (registered by Conditions.ts on import) */
export function getAbilityConditions(): Record<ConditionKey, (ctx: ActionContext) => boolean> {
  return _globalThis.__HELIX_CONDITIONS ?? {} as Record<ConditionKey, (ctx: ActionContext) => boolean>;
}

/** Register element configs on globalThis (called by ElementRegistry.ts) */
export function setAbilityElements(elements: Record<ElementType, ElementConfig>): void {
  _globalThis.__HELIX_ELEMENTS = elements;
}

/** Register condition evaluators on globalThis (called by Conditions.ts) */
export function setAbilityConditions(conditions: Record<ConditionKey, (ctx: ActionContext) => boolean>): void {
  _globalThis.__HELIX_CONDITIONS = conditions;
}
